import { useState } from "react";
import { ArrowDownRight, ArrowUpRight, BusFront, Check, HeartPulse, MapPin, Navigation, ShoppingBag, Trees } from "lucide-react";
import Reveal from "./Reveal";
import { developmentLocation, nearbyCategories, nearbyPlaces, mapEmbedUrl, mapSearchUrl } from "../data/nearby";
import "./location.css";

const categoryIcons = { mobilidade: BusFront, compras: ShoppingBag, saude: HeartPulse, lazer: Trees };

export default function LocationSection() {
  const [category, setCategory] = useState("todos");
  const [selected, setSelected] = useState(developmentLocation);
  const [mapMode, setMapMode] = useState("map");
  const places = category === "todos" ? nearbyPlaces : nearbyPlaces.filter((place) => place.category === category);

  function selectPlace(place) {
    setSelected(place);
    setMapMode("map");
  }

  function selectCategory(next) {
    setCategory(next);
    if (next !== "todos" && selected.category !== next) {
      selectPlace(nearbyPlaces.find((place) => place.category === next));
    }
  }

  return (
    <section className="lc-location" id="localizacao" aria-labelledby="location-heading">
      <div className="lc-location__container">
        <Reveal className="lc-location__intro">
          <div>
            <span className="lc-location__eyebrow"><MapPin size={15} aria-hidden="true" /> Novo Mundo · Curitiba</span>
            <h2 id="location-heading">A cidade acontece.<br /><em>Você está perto.</em></h2>
          </div>
          <p>Compras, serviços, mobilidade e cultura fazem parte do seu entorno. Um endereço para viver o Novo Mundo com mais praticidade.</p>
        </Reveal>

        <Reveal delay={0.08} className="lc-location__address">
          <div className="lc-location__pin"><MapPin size={23} aria-hidden="true" /></div>
          <div><strong>La Courbe Tower</strong><span>{developmentLocation.address}</span></div>
          <button type="button" onClick={() => selectPlace(developmentLocation)} aria-label="Mostrar endereço do La Courbe no mapa">Ver endereço <ArrowDownRight size={18} aria-hidden="true" /></button>
        </Reveal>

        <Reveal delay={0.12} className="lc-location__explorer">
          <div className="lc-location__map-area">
            <div className="lc-location__map-tabs" aria-label="Visualização da localização">
              <button type="button" aria-pressed={mapMode === "map"} onClick={() => setMapMode("map")}>Mapa interativo</button>
              <button type="button" aria-pressed={mapMode === "aerial"} onClick={() => setMapMode("aerial")}>Vista aérea</button>
            </div>
            <div className="lc-location__map-canvas">
              {mapMode === "map" ? (
                <iframe
                  key={selected.id}
                  src={mapEmbedUrl(selected.query)}
                  title={`Mapa de ${selected.name}`}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  onError={() => setMapMode("aerial")}
                />
              ) : (
                <figure className="lc-location__aerial">
                  <img src="/assets/location-map.webp" alt="Perspectiva aérea do La Courbe Tower e seu quarteirão, extraída do book do empreendimento" loading="lazy" />
                  <figcaption>Perspectiva ilustrativa do empreendimento.</figcaption>
                </figure>
              )}
            </div>
            <div className="lc-location__map-footer">
              <div aria-live="polite"><MapPin size={17} aria-hidden="true" /><span>{mapMode === "aerial" ? "La Courbe Tower · vista aérea" : selected.name}</span></div>
              <a href={mapSearchUrl(mapMode === "aerial" ? developmentLocation.query : selected.query)} target="_blank" rel="noopener noreferrer">Abrir no Google Maps <ArrowUpRight size={17} aria-hidden="true" /></a>
            </div>
          </div>

          <aside className="lc-location__nearby" aria-label="Pontos de interesse na região">
            <div className="lc-location__nearby-title"><span>Explore o entorno</span><Navigation size={19} aria-hidden="true" /></div>
            <div className="lc-location__filters" role="group" aria-label="Filtrar lugares por categoria">
              {nearbyCategories.map((item) => (
                <button key={item.id} type="button" onClick={() => selectCategory(item.id)} aria-pressed={category === item.id}>{item.label}</button>
              ))}
            </div>
            <ul className="lc-location__places">
              {places.map((place) => {
                const Icon = categoryIcons[place.category];
                const active = selected.id === place.id;
                return (
                  <li key={place.id}>
                    <button type="button" className={active ? "is-selected" : ""} aria-pressed={active} onClick={() => selectPlace(place)}>
                      <span className="lc-location__place-icon"><Icon size={19} aria-hidden="true" /></span>
                      <span className="lc-location__place-copy"><strong>{place.name}</strong><small>{place.detail}</small></span>
                      {active ? <Check className="lc-location__place-arrow" size={16} aria-hidden="true" /> : <ArrowUpRight className="lc-location__place-arrow" size={16} aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="lc-location__hint">Selecione um lugar para explorar no mapa.</p>
          </aside>
        </Reveal>

        <Reveal className="lc-location__closing" delay={0.18}>
          <p><strong>Um endereço que acompanha seu ritmo.</strong> Conheça as possibilidades do La Courbe.</p>
          <a href="#contato">Quero conhecer <ArrowUpRight size={19} aria-hidden="true" /></a>
        </Reveal>
      </div>
    </section>
  );
}
